// src/components/Admin/Enquiries.jsx
import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import Layout from "./Layout";
import {
  Search,
  Mail,
  Phone,
  Calendar,
  Trash2,
  CheckCircle2,
  X,
  AlertTriangle,
  HelpCircle,
  Eye,
  RefreshCw,
} from "lucide-react";
import {
  fetchAdminEnquiries,
  updateEnquiryResolveStatus,
  deleteAdminEnquiry,
} from "../Services/userServices";

const Enquiries = () => {
  const [enquiries, setEnquiries] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeFilter, setActiveFilter] = useState("All");
  const [selectedEnquiry, setSelectedProject] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");

  const loadEnquiries = async () => {
    setIsLoading(true);
    setError("");
    const filters = {
      search: searchQuery,
      resolved: activeFilter,
    };
    const result = await fetchAdminEnquiries(filters);
    if (result.success) {
      setEnquiries(result.data || []);
    } else {
      setError(result.error || "Failed to load inquiries.");
    }
    setIsLoading(false);
  };

  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      loadEnquiries();
    }, 400);
    return () => clearTimeout(delayDebounce);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, activeFilter]);

  const handleToggleResolve = async (id, currentStatus) => {
    const nextStatus = !currentStatus;
    const result = await updateEnquiryResolveStatus(id, nextStatus);
    if (result.success) {
      setEnquiries((prev) =>
        prev.map((item) =>
          item.id === id ? { ...item, is_resolved: result.is_resolved } : item,
        ),
      );
      if (selectedEnquiry && selectedEnquiry.id === id) {
        setSelectedProject((prev) => ({
          ...prev,
          is_resolved: result.is_resolved,
        }));
      }
    } else {
      alert("Failed to update status.");
    }
  };

  const executeDelete = async () => {
    if (!deleteId) return;
    const result = await deleteAdminEnquiry(deleteId);
    if (result.success) {
      setEnquiries((prev) => prev.filter((item) => item.id !== deleteId));
      setDeleteId(null);
    } else {
      alert(result.error || "Failed to delete.");
    }
  };

  return (
    <Layout>
      <div className="space-y-8 pb-12">
        {/* HEADER */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900 dark:text-white">
              Customer Enquiries
            </h1>
            <p className="text-slate-500 dark:text-slate-400 text-sm mt-1">
              Read and resolve general inquiries received from the Contact Us
              form
            </p>
          </div>
          <button
            onClick={loadEnquiries}
            className="flex items-center gap-2 px-4 py-2 bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300 font-bold rounded-xl transition text-xs border border-slate-200 dark:border-slate-700 cursor-pointer"
          >
            <RefreshCw className="w-3.5 h-3.5" /> Reload List
          </button>
        </div>

        {/* SEARCH & FILTER CONTROLS */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-white dark:bg-slate-900/50 p-4 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm">
          <div className="relative flex-1 max-w-md">
            <Search className="w-4 h-4 text-slate-400 absolute left-3.5 top-3.5" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by name, email, message subject..."
              className="w-full pl-10 pr-4 py-2.5 bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-white border border-slate-200 dark:border-slate-700 rounded-xl focus:ring-2 focus:ring-[#10B981] outline-none text-xs font-medium"
            />
          </div>

          <div className="flex items-center gap-1.5 overflow-x-auto pb-1 md:pb-0">
            {["All", "Pending", "Resolved"].map((status) => (
              <button
                key={status}
                onClick={() => setActiveFilter(status)}
                className={`px-3.5 py-2 rounded-xl text-xs font-bold whitespace-nowrap transition-all cursor-pointer ${
                  activeFilter === status
                    ? "bg-slate-900 text-white dark:bg-emerald-500 dark:text-white shadow-sm"
                    : "bg-slate-50 text-slate-600 hover:bg-slate-100 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700 border border-slate-200 dark:border-slate-700/60"
                }`}
              >
                {status}
              </button>
            ))}
          </div>
        </div>

        {/* ERROR BANNERS */}
        {error && (
          <div className="p-4 rounded-2xl bg-red-500/10 border border-red-500/20 text-red-600 dark:text-red-400 font-bold text-sm flex items-center gap-3">
            <AlertTriangle className="w-5 h-5" />
            <span>{error}</span>
          </div>
        )}

        {/* DATA TABLE / SPINNER */}
        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <div className="w-8 h-8 border-4 border-[#10B981] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : enquiries.length === 0 ? (
          <div className="p-16 text-center bg-white dark:bg-slate-900/50 rounded-2xl border border-slate-200 dark:border-slate-800">
            <Mail className="w-10 h-10 text-slate-400 mx-auto mb-3 opacity-60" />
            <h3 className="text-base font-bold text-slate-800 dark:text-slate-200">
              No Enquiries Found
            </h3>
            <p className="text-xs text-slate-500 mt-1">
              All quiet! No pending customer messages found matching these
              parameters
            </p>
          </div>
        ) : (
          <div className="bg-white dark:bg-slate-900/50 rounded-2xl shadow-sm dark:shadow-2xl border border-slate-200 dark:border-slate-800 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead className="bg-slate-50 dark:bg-slate-950/80 text-slate-600 dark:text-slate-400 border-b border-slate-200 dark:border-slate-800">
                  <tr>
                    <th className="px-6 py-4 text-xs font-bold uppercase tracking-wider">
                      Sender
                    </th>
                    <th className="px-6 py-4 text-xs font-bold uppercase tracking-wider">
                      Email
                    </th>
                    <th className="px-6 py-4 text-xs font-bold uppercase tracking-wider">
                      Phone
                    </th>
                    <th className="px-6 py-4 text-xs font-bold uppercase tracking-wider">
                      Subject
                    </th>
                    <th className="px-6 py-4 text-xs font-bold uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-4 text-xs font-bold uppercase tracking-wider">
                      Submitted On
                    </th>
                    <th className="px-6 py-4 text-xs font-bold uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200 dark:divide-slate-800/60">
                  {enquiries.map((item) => (
                    <tr
                      key={item.id}
                      className="hover:bg-slate-50/50 dark:hover:bg-slate-900/20 transition-colors"
                    >
                      <td className="px-6 py-4 text-sm font-bold text-slate-900 dark:text-white">
                        {item.name}
                      </td>
                      <td className="px-6 py-4 text-sm">
                        <div className="flex items-center gap-1.5 text-slate-700 dark:text-slate-300 font-semibold">
                          <Mail className="w-3.5 h-3.5 text-slate-400" />{" "}
                          {item.email}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm">
                        <div className="flex items-center gap-1.5 text-slate-400 dark:text-slate-500 font-medium">
                          <Phone className="w-3 h-3 text-slate-400" />{" "}
                          {item.phone || "—"}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm max-w-xs">
                        <div className="font-bold text-slate-800 dark:text-slate-200 truncate">
                          {item.subject}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <button
                          onClick={() =>
                            handleToggleResolve(item.id, item.is_resolved)
                          }
                          className={`px-2.5 py-1 text-[10px] font-extrabold uppercase tracking-widest rounded-full cursor-pointer transition ${
                            item.is_resolved
                              ? "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20"
                              : "bg-amber-500/10 text-amber-600 dark:text-amber-400 border border-amber-500/20"
                          }`}
                          title="Click to toggle status"
                        >
                          {item.is_resolved ? "Resolved" : "Pending"}
                        </button>
                      </td>
                      <td className="px-6 py-4 text-xs text-slate-400 dark:text-slate-500 font-medium">
                        <div className="flex items-center gap-1.5">
                          <Calendar className="w-3.5 h-3.5" />
                          {new Date(item.created_at).toLocaleDateString(
                            "en-IN",
                            {
                              day: "numeric",
                              month: "short",
                              year: "numeric",
                            },
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => setSelectedProject(item)}
                            className="p-2 bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-600 dark:text-emerald-400 rounded-xl border border-emerald-500/20 transition cursor-pointer"
                            title="Read full message"
                          >
                            <Eye className="w-3.5 h-3.5" />
                          </button>
                          <button
                            onClick={() => setDeleteId(item.id)}
                            className="p-2 bg-red-500/10 hover:bg-red-500/20 text-red-600 dark:text-red-400 rounded-xl border border-red-500/20 transition cursor-pointer"
                            title="Delete"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* 🌟 ENQUIRY QUICK VIEW DETAILS MODAL — Portal to document.body */}
      {selectedEnquiry &&
        createPortal(
          <div className="fixed inset-0 z-[99999] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fade-in">
            <div className="bg-white dark:bg-slate-900 rounded-3xl border border-slate-200 dark:border-slate-800 shadow-2xl max-w-xl w-full p-6 sm:p-8 space-y-6">
              <div className="flex items-start justify-between gap-4 border-b border-slate-100 dark:border-slate-800/80 pb-4">
                <div>
                  <span
                    className={`px-2.5 py-1 text-[10px] font-extrabold uppercase tracking-widest rounded-md border ${
                      selectedEnquiry.is_resolved
                        ? "bg-emerald-500/10 text-[#10B981] border-emerald-500/20"
                        : "bg-amber-500/10 text-amber-500 border-amber-500/20"
                    }`}
                  >
                    {selectedEnquiry.is_resolved
                      ? "Resolved"
                      : "Pending Action"}
                  </span>
                  <h2 className="text-xl font-extrabold text-slate-900 dark:text-white mt-3">
                    {selectedEnquiry.subject}
                  </h2>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                    From:{" "}
                    <strong className="text-slate-700 dark:text-slate-300">
                      {selectedEnquiry.name}
                    </strong>{" "}
                    • Email: {selectedEnquiry.email}
                  </p>
                </div>
                <button
                  onClick={() => setSelectedProject(null)}
                  className="p-2 rounded-xl bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-500 dark:text-slate-400 cursor-pointer"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="space-y-2">
                <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400">
                  Message Body
                </h4>
                <div className="p-4 rounded-2xl bg-slate-50 dark:bg-slate-800/50 border border-slate-200/60 dark:border-slate-700/60 text-sm text-slate-700 dark:text-slate-300 leading-relaxed font-medium min-h-[100px] whitespace-pre-wrap">
                  {selectedEnquiry.message}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3 text-xs p-4 rounded-xl bg-slate-50 dark:bg-slate-800/20 border border-slate-100 dark:border-slate-800/80">
                <div>
                  <span className="text-slate-400 block text-[11px]">
                    Phone Number:
                  </span>
                  <strong className="text-slate-800 dark:text-slate-200">
                    {selectedEnquiry.phone || "Not Provided"}
                  </strong>
                </div>
                <div>
                  <span className="text-slate-400 block text-[11px]">
                    Received On:
                  </span>
                  <strong className="text-slate-800 dark:text-slate-200">
                    {new Date(selectedEnquiry.created_at).toLocaleString(
                      "en-IN",
                      {
                        day: "numeric",
                        month: "short",
                        year: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                      },
                    )}
                  </strong>
                </div>
              </div>

              <div className="flex items-center justify-between gap-3 pt-2">
                <button
                  onClick={() =>
                    handleToggleResolve(
                      selectedEnquiry.id,
                      selectedEnquiry.is_resolved,
                    )
                  }
                  className={`px-5 py-2.5 rounded-xl text-xs font-bold transition-all cursor-pointer border ${
                    selectedEnquiry.is_resolved
                      ? "bg-slate-100 border-slate-200 text-slate-700 hover:bg-slate-200 dark:bg-slate-800 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-700"
                      : "bg-emerald-600 border-emerald-500 text-white hover:bg-emerald-500 shadow-md shadow-emerald-500/10"
                  }`}
                >
                  {selectedEnquiry.is_resolved
                    ? "Mark Unresolved"
                    : "Mark as Resolved"}
                </button>
                <button
                  onClick={() => setSelectedProject(null)}
                  className="px-5 py-2.5 rounded-xl bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300 font-bold text-xs transition cursor-pointer"
                >
                  Close Window
                </button>
              </div>
            </div>
          </div>,
          document.body,
        )}

      {/* 🗑️ DELETE CONFIRMATION — Portal to document.body */}
      {deleteId &&
        createPortal(
          <div className="fixed inset-0 z-[99999] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fade-in">
            <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-6 rounded-2xl max-w-sm w-full shadow-2xl text-center space-y-5">
              <div className="w-12 h-12 bg-red-500/10 text-red-500 rounded-full flex items-center justify-center mx-auto border border-red-500/20">
                <Trash2 className="w-5 h-5 animate-pulse" />
              </div>

              <div className="space-y-1">
                <h3 className="text-base font-extrabold text-slate-900 dark:text-white">
                  Delete Enquiry?
                </h3>
                <p className="text-xs text-slate-500 dark:text-slate-400 leading-relaxed">
                  Are you sure you want to permanently delete this message? This
                  action is irreversible.
                </p>
              </div>

              <div className="flex gap-3 pt-1">
                <button
                  onClick={() => setDeleteId(null)}
                  className="flex-1 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 text-xs font-bold transition hover:bg-slate-100 dark:hover:bg-slate-700 cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  onClick={executeDelete}
                  className="flex-1 py-2.5 rounded-xl bg-red-600 hover:bg-red-500 text-white text-xs font-bold shadow-md shadow-red-500/20 transition cursor-pointer"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>,
          document.body,
        )}
    </Layout>
  );
};

export default Enquiries;
